import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, Save, Send, AlertCircle, Search, Download, Trash2, Archive } from "lucide-react";
import JSZip from "jszip";
import { ProjectService } from "../services/projectService";
import { formatDate } from "../utils/dateFormatting";
import { formatTextWithLinks } from "../utils/linkFormatter";
import { toCdnDownloadUrl } from "../utils/fileUtils";
import { Modal, ModalBody, ModalFooter, Button, Input, Dropdown } from "./ui";

const ModelQuotesModal = ({
  quote,
  clients,
  contacts,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    name: "",
    project_name: "",
    client_name: "",
    selectedClientId: "",
    selectedContactId: "",
    quote_due_date: "",
    additional_requests: [],
    project_info: "",
    price: "",
    notes_from_ab_civil: "",
    quote_status: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dropdownRef = useRef(null);

  // Filter contacts based on selected client
  const filteredContacts = useMemo(() => {
    if (!formData.selectedClientId || !contacts) return [];
    console.log("Filtering contacts for clientId:", formData.selectedClientId);
    console.log("All contacts:", contacts);
    const filtered = contacts.filter((contact) => {
      // Handle both string and number comparisons
      return (
        String(contact.clientId) === String(formData.selectedClientId) ||
        String(contact.client_id) === String(formData.selectedClientId)
      );
    });
    console.log("Filtered contacts:", filtered);
    return filtered;
  }, [formData.selectedClientId, contacts]);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!clientSearch) return clients;
    const searchLower = clientSearch.toLowerCase();
    return clients.filter((client) =>
      client.name?.toLowerCase().includes(searchLower),
    );
  }, [clients, clientSearch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showClientDropdown]);

  useEffect(() => {
    if (quote) {
      setFormData({
        company_name: quote.company_name || "",
        name: quote.name || "",
        project_name: quote.project_name || "",
        client_name: quote.client_name || "",
        selectedClientId: quote.client_id || "",
        selectedContactId: quote.contact_id || "",
        quote_due_date: quote.quote_due_date
          ? formatDate(quote.quote_due_date, "input")
          : "",
        additional_requests: quote.additional_requests || [],
        project_info: quote.project_info || "",
        price: quote.price || "",
        notes_from_ab_civil: quote.notes_from_ab_civil || "",
        quote_status: quote.quote_status || "",
      });

      // Set client search value and find client if needed
      if (clients) {
        let selectedClient = null;
        
        // First try to use existing client_id
        if (quote.client_id) {
          selectedClient = clients.find((c) => c.id === quote.client_id);
        }
        
        // If no client_id, try to match by client_name
        if (!selectedClient && quote.client_name) {
          selectedClient = clients.find(
            (c) => c.name?.toLowerCase() === quote.client_name?.toLowerCase(),
          );
          // Only update the form data if we found a match by name and didn't have an ID
          if (selectedClient) {
            setFormData((prev) => ({
              ...prev,
              selectedClientId: selectedClient.id,
            }));
          }
        }
        
        // Set the search field to show the client name
        if (selectedClient) {
          setClientSearch(selectedClient.name);
        }
      }
    }
  }, [quote, clients, contacts]);

  const handleClientSelect = (client) => {
    const newFormData = {
      ...formData,
      selectedClientId: client.id,
      selectedContactId: "", // Clear contact when client changes
      client_name: client.name, // Update client_name when a client is selected
    };

    setFormData(newFormData);
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };

    // Clear contact when client changes via dropdown
    if (field === "selectedClientId") {
      newFormData.selectedContactId = "";
      
      // Update client_name when a client is selected via dropdown
      if (value) {
        const selectedClient = clients.find((c) => c.id === parseInt(value));
        if (selectedClient) {
          newFormData.client_name = selectedClient.name;
          setClientSearch(selectedClient.name);
        }
      }
    }

    setFormData(newFormData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        company_name: formData.company_name || null,
        name: formData.name || null,
        project_name: formData.project_name || null,
        client_name: formData.client_name || null,
        client_id: formData.selectedClientId ? parseInt(formData.selectedClientId) : null,
        contact_id: formData.selectedContactId ? parseInt(formData.selectedContactId) : null,
        quote_due_date: formData.quote_due_date || null,
        additional_requests:
          formData.additional_requests.length > 0
            ? formData.additional_requests
            : null,
        project_info: formData.project_info || null,
        price: formData.price || null,
        notes_from_ab_civil: formData.notes_from_ab_civil || null,
        quote_status: formData.quote_status || null,
      };

      await ProjectService.updateModelQuoteBatch(quote.id, fieldsToUpdate);

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving model quote:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        company_name: formData.company_name || null,
        name: formData.name || null,
        project_name: formData.project_name || null,
        client_name: formData.client_name || null,
        client_id: formData.selectedClientId ? parseInt(formData.selectedClientId) : null,
        contact_id: formData.selectedContactId ? parseInt(formData.selectedContactId) : null,
        quote_due_date: formData.quote_due_date || null,
        additional_requests:
          formData.additional_requests.length > 0
            ? formData.additional_requests
            : null,
        project_info: formData.project_info || null,
        price: formData.price || null,
        notes_from_ab_civil: formData.notes_from_ab_civil || null,
        quote_status: "Quoted", // Always set to Quoted when using Save and Send
      };

      await ProjectService.updateModelQuoteBatch(quote.id, fieldsToUpdate);

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving and sending model quote:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileDownload = (fileUrl, fileName) => {
    // Create a temporary link to download the file (via Cloudflare edge)
    const link = document.createElement('a');
    link.href = toCdnDownloadUrl(fileUrl);
    link.download = fileName || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadAll = async () => {
    if (!quote?.file_attachments?.length) return;
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      const usedNames = {};

      await Promise.all(
        quote.file_attachments.map(async (file, index) => {
          const url = file.url || file.path || file;
          let name = file.filename || file.name || `Attachment_${index + 1}`;

          // Deduplicate filenames
          if (usedNames[name]) {
            const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
            const base = ext ? name.slice(0, -ext.length) : name;
            name = `${base}_${usedNames[name]}${ext}`;
            usedNames[file.filename || file.name || `Attachment_${index + 1}`]++;
          } else {
            usedNames[name] = 1;
          }

          const response = await fetch(toCdnDownloadUrl(url));
          const blob = await response.blob();
          zip.file(name, blob);
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipName = `${quote.project_name || 'attachments'}.zip`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Error creating zip:', err);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);

      console.log("Attempting to delete model quote:", quote.id, quote.project_name);
      
      const result = await ProjectService.deleteModelQuote(quote.id);
      console.log("Delete result:", result);

      console.log("Calling onSave callback to refresh parent component");
      if (onSave) {
        await onSave();
      }
      
      console.log("Closing modal after successful delete");
      onClose();
    } catch (err) {
      console.error("Error deleting model quote:", err);
      setError(`Failed to delete quote: ${err.message}`);
      // Don't close the modal on error, let user see the error
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal trackAs="ModelQuotesModal" isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false}>
        {/* Custom Header with Quote Info */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            {/* Project Name */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formData.project_name || 'Model Quote Request'}
              </h1>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 'var(--space-2)',
                cursor: 'pointer',
                color: 'var(--gray-400)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gray-700)';
                e.currentTarget.style.color = 'var(--gray-50)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--gray-400)';
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <ModalBody>
          {error && (
            <div className="modal-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* LEFT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {/* QUOTE INFORMATION SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Quote Information
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <Input
                    label="Project Name"
                    value={formData.project_name}
                    onChange={(e) => handleInputChange("project_name", e.target.value)}
                    placeholder="Enter project name"
                    required
                  />

                  <Input
                    label="Quote Due Date"
                    type="date"
                    value={formData.quote_due_date}
                    onChange={(e) => handleInputChange("quote_due_date", e.target.value)}
                  />

                  <Input
                    label="Price"
                    type="text"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    placeholder="Enter price"
                  />

                  <Dropdown
                    label="Quote Status"
                    options={[
                      { value: "", label: "Select status" },
                      { value: "To Quote", label: "To Quote" },
                      { value: "Quoted", label: "Quoted" },
                      { value: "Accepted", label: "Accepted" },
                      { value: "Declined", label: "Declined" }
                    ]}
                    value={formData.quote_status}
                    onChange={(value) => handleInputChange("quote_status", value)}
                    placeholder="Select status"
                  />
                </div>
              </div>

              {/* FALLBACK INFORMATION SECTION - Only show if no client selected */}
              {!formData.selectedClientId && (
                <div>
                  <h2 style={{
                    margin: 0,
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)',
                  }}>
                    Form Submission Info
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--gray-300)',
                        }}
                      >
                        Form Company Name
                      </label>
                      <div
                        style={{
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-200)',
                          fontSize: 'var(--text-sm)',
                        }}
                      >
                        {formData.company_name || "Not provided"}
                      </div>
                    </div>

                    {!formData.selectedContactId && (
                      <div>
                        <label
                          style={{
                            display: 'block',
                            marginBottom: 'var(--space-2)',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                            color: 'var(--gray-300)',
                          }}
                        >
                          Form Contact Name
                        </label>
                        <div
                          style={{
                            padding: 'var(--space-3)',
                            backgroundColor: 'var(--dark-elevated)',
                            border: '1px solid var(--gray-700)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--gray-200)',
                            fontSize: 'var(--text-sm)',
                          }}
                        >
                          {formData.name || "Not provided"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {/* CLIENT & CONTACT SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Client & Contact
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  {/* Client Search */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Client
                    </label>
                    <div className="searchable-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={16} style={{
                          position: 'absolute',
                          left: 'var(--space-3)',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--gray-400)',
                          pointerEvents: 'none'
                        }} />
                        <input
                          type="text"
                          value={clientSearch}
                          onChange={(e) => {
                            setClientSearch(e.target.value);
                            setShowClientDropdown(true);
                          }}
                          onFocus={() => setShowClientDropdown(true)}
                          onClick={() => setShowClientDropdown(true)}
                          placeholder="Search for client..."
                          autoComplete="off"
                          style={{
                            width: '100%',
                            padding: 'var(--space-3)',
                            paddingLeft: 'calc(var(--space-3) + 24px)',
                            fontSize: 'var(--text-sm)',
                            border: '1px solid var(--gray-600)',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'var(--dark-elevated)',
                            color: 'var(--gray-50)',
                            transition: 'all var(--transition-fast)',
                          }}
                        />
                      </div>
                      {showClientDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          background: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                        }}>
                          {filteredClients.length > 0 ? (
                            filteredClients.map((client) => (
                              <div
                                key={client.id}
                                onClick={() => handleClientSelect(client)}
                                style={{
                                  padding: 'var(--space-3)',
                                  cursor: 'pointer',
                                  color: 'var(--gray-200)',
                                  fontSize: 'var(--text-sm)',
                                  borderBottom: '1px solid var(--gray-700)',
                                  transition: 'all var(--transition-fast)',
                                  backgroundColor: 'var(--dark-elevated)',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                                  e.currentTarget.style.color = 'var(--gray-50)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                                  e.currentTarget.style.color = 'var(--gray-200)';
                                }}
                              >
                                {client.name}
                              </div>
                            ))
                          ) : (
                            <div style={{
                              padding: 'var(--space-3)',
                              color: 'var(--gray-400)',
                              fontSize: 'var(--text-sm)',
                              textAlign: 'center'
                            }}>
                              No clients found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Dropdown */}
                  <Dropdown
                    label="Project Contact"
                    options={[
                      { value: "", label: formData.selectedClientId ? "Select Contact..." : "Select client first..." },
                      ...filteredContacts.map((contact) => ({ value: contact.id, label: contact.name }))
                    ]}
                    value={formData.selectedContactId}
                    onChange={(value) => handleInputChange("selectedContactId", value)}
                    disabled={!formData.selectedClientId}
                    placeholder="Select contact"
                  />
                </div>
              </div>

              {/* FILE ATTACHMENTS SECTION */}
              {quote?.file_attachments && quote.file_attachments.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                    <h2 style={{
                      margin: 0,
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}>
                      File Attachments ({quote.file_attachments.length})
                    </h2>
                    <button
                      type="button"
                      onClick={handleDownloadAll}
                      disabled={downloadingAll}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--cta-blue)',
                        fontSize: 'var(--text-sm)',
                        cursor: downloadingAll ? 'not-allowed' : 'pointer',
                        opacity: downloadingAll ? 0.6 : 1,
                        transition: 'all var(--transition-fast)',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        if (!downloadingAll) {
                          e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                          e.currentTarget.style.borderColor = 'var(--cta-blue)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                        e.currentTarget.style.borderColor = 'var(--gray-600)';
                      }}
                    >
                      <Archive size={14} />
                      {downloadingAll ? 'Zipping...' : 'Download All'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {quote.file_attachments.map((file, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleFileDownload(
                          file.url || file.path || file,
                          file.filename || file.name || `Attachment ${index + 1}`
                        )}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--cta-blue)',
                          fontSize: 'var(--text-sm)',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                          e.currentTarget.style.borderColor = 'var(--cta-blue)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                          e.currentTarget.style.borderColor = 'var(--gray-600)';
                        }}
                      >
                        <Download size={14} />
                        {file.filename || file.name || `Attachment ${index + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ADDITIONAL FEATURES SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Additional Features
                </h2>
                <div
                  style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                    minHeight: '50px',
                  }}
                >
                  {Array.isArray(formData.additional_requests) && formData.additional_requests.length > 0
                    ? formData.additional_requests.join(", ")
                    : "None selected"}
                </div>
              </div>
            </div>
          </div>

          {/* FULL WIDTH SECTIONS BELOW COLUMNS */}

          {/* Additional Notes - Full Width */}
          {formData.project_info && (
            <div style={{ marginTop: 'var(--space-6)' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Additional Notes
              </label>
              <div
                style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-200)',
                  fontSize: 'var(--text-sm)',
                  minHeight: '80px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formData.project_info
                  ? formatTextWithLinks(formData.project_info)
                  : "No additional information provided"
                }
              </div>
            </div>
          )}

          {/* Notes from AB Civil - Full Width */}
          <div style={{ marginTop: 'var(--space-6)' }}>
            <label
              style={{
                display: 'block',
                marginBottom: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--gray-300)',
              }}
            >
              Notes from AB Civil
            </label>
            <textarea
              value={formData.notes_from_ab_civil}
              onChange={(e) =>
                handleInputChange("notes_from_ab_civil", e.target.value)
              }
              placeholder="Enter notes from AB Civil"
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--gray-600)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-50)',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            {showDeleteConfirm ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <span style={{ color: 'var(--danger)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>
                  Delete this quote?
                </span>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Yes, Delete"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving || deleting}
              >
                <Trash2 size={16} />
                Delete Quote
              </Button>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || deleting}
              >
                <Save size={16} />
                {saving ? "Saving..." : "Save Changes"}
              </Button>

              {/* Save and Send button */}
              {formData.quote_status !== "Quoted" && (
                <Button
                  variant="primary"
                  onClick={handleSaveAndSend}
                  disabled={saving || deleting}
                  style={{
                    background: 'var(--cta-blue)',
                  }}
                >
                  <Send size={16} />
                  {saving ? "Sending..." : "Save and Send"}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ModelQuotesModal;
